import * as React from 'react';
import {useEffect, useRef} from 'react';
import {FormattedMessage} from "react-intl";

import {
    useResidenceExplorerStore,
    selectCurrentView,
    selectResidenceExplorerSettings,
    selectSetup,
    selectShowSunStudy,
} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {isDefined} from "features/services/utils/helpers";
import {ImageSizes} from "@organisms/Shell/image-size-enum";
import {useDeviceStore, selectIsMobile, selectIsTablet} from "features/ui/store/useDeviceStore";

import './_residenceExplorerView.scss';

import {Icon} from "@atoms/Media/Icon";
import {Button, ButtonColor, ButtonSize, ButtonType} from "@atoms/LinksAndButtons/Button";
import {UserAgent} from "@atoms/UserAgent";
import {AsyncImage} from "@atoms/Media/AsyncImage";
import {SliderArrow} from "@atoms/LinksAndButtons/SliderArrow";

import {SelectedObjectPopup} from "features/residenceExplorer/components/ResidenceExplorerV2/_selectedObjectPopup";
import {
    ResidenceExplorerClickArea
} from "features/residenceExplorer/components/ResidenceExplorerV2/_residenceExplorerClickArea";
import {
    ResidenceExplorerSunStudyView
} from "features/residenceExplorer/components/ResidenceExplorerV2/_residenceExplorerSunStudyView";
import {EpiImage} from "features/search/types";


export const ResidenceExplorerView: React.FunctionComponent = () => {
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);
    const settings = useResidenceExplorerStore(selectResidenceExplorerSettings);
    const setup = useResidenceExplorerStore(selectSetup);
    const currentView = useResidenceExplorerStore(selectCurrentView);
    const showSunStudy = useResidenceExplorerStore(selectShowSunStudy);
    const setCurrentViewName = useResidenceExplorerStore(state => state.setCurrentViewName);
    const setSelectedObjectId = useResidenceExplorerStore(state => state.setSelectedObjectId);
    const viewReference = useRef<HTMLDivElement>(null);
    const scrollReference = useRef<HTMLDivElement>(null);

    const { viewBoxHeight, viewBoxWidth } = settings ?? { viewBoxHeight: 0, viewBoxWidth: 0};

    const residenceExplorerImage = React.useMemo<EpiImage | undefined>(() => {

        if (!setup || !currentView) {
            return undefined;
        }

        const { stageId } = setup;
        const { groupName, imageSrc, localHiResImageSrc } = currentView;

        const imageUrl = localHiResImageSrc
            ? localHiResImageSrc
            : `/externalfiles/image/residenceexplorer/${stageId}/${groupName}${imageSrc}`;

        return {
            url: imageUrl,
            orgHeight: viewBoxHeight,
            orgWidth: viewBoxWidth,
            ratio: viewBoxWidth / viewBoxHeight,
        };
    }, [setup, currentView, viewBoxHeight, viewBoxWidth]);



    const mobileImageDimensions = () => {
        let imageWidth = 0;
        let imageHeight = 400;
        const imageWidthCalculation = imageHeight / (viewBoxHeight / viewBoxWidth);

        if (scrollReference.current !== null) {
            imageWidth = (imageWidthCalculation > scrollReference.current.clientWidth) ?
                imageWidthCalculation :
                scrollReference.current.clientWidth;
            imageHeight = (imageWidthCalculation > scrollReference.current.clientWidth) ?
                imageHeight :
                (viewBoxHeight / viewBoxWidth) * scrollReference.current.clientWidth
        }

        return {
            width: imageWidth,
            height: imageHeight,
        }
    };

    // Center the scroll position on mobile so the building is visible
    useEffect(() => {
        if (isMobile && !isTablet && scrollReference.current) {
            requestAnimationFrame(() => {
                if (scrollReference.current) {
                    const { scrollWidth, clientWidth } = scrollReference.current;
                    if (scrollWidth > clientWidth) {
                        scrollReference.current.scrollLeft = (scrollWidth - clientWidth) / 2;
                    }
                }
            });
        }
    }, [currentView, isMobile, isTablet, viewBoxWidth, viewBoxHeight]);

    const getParentView = () => {

        if(!settings) return '';

        if (currentView && currentView.parentViewId != null  && currentView.parentViewId !== "0") return currentView.parentViewId.toLowerCase();
        return settings.firstView.toLowerCase();
    };

    const getCurrentParentView = () => {
        if (!currentView || currentView.parentViewId === null || currentView.parentViewId === "0") return undefined;
        return currentView.parentViewId;
    };

    const goBack = () => {
        const targetView = getParentView();



        setSelectedObjectId('');
        setCurrentViewName(targetView);
    };

    const onPreviousViewClick = () => {

        if(!currentView) return;

        setSelectedObjectId('');
        setCurrentViewName(currentView.interLink[0].value);
    };

    const onNextViewClick = () => {
        if(!currentView) return;

        setSelectedObjectId('');
        setCurrentViewName(currentView.interLink[1].value);
    };

    return (
        <div className="residence-explorer-view" ref={viewReference}>
            {((typeof getCurrentParentView() === 'undefined' && currentView && settings &&  currentView.viewId !== settings.firstView) ||
             (typeof getCurrentParentView() !== 'undefined' && currentView && currentView.viewId !== getCurrentParentView())) &&
                <Button
                    className="residence-explorer-view__back"
                    onClick={goBack}
                    icon={<Icon name="arrow-link" size={24}/>}
                    iconFirst
                    buttonColor={ButtonColor.DEFAULT}
                    buttonSize={ButtonSize.DEFAULT}
                    buttonType={ButtonType.DEFAULT}
                >
                    <span>
                        <FormattedMessage id="jm.components.ResetPasswordForm.backToForm" />
                    </span>
                </Button>
            }

            <div className="residence-explorer-view__scroll-container" ref={scrollReference}>
                <div
                    style={(isMobile && !isTablet) ? mobileImageDimensions() : {}}
                >
                    {!showSunStudy &&
                        <AsyncImage
                            image={residenceExplorerImage}
                            quality={ImageSizes.SIZE_1200}
                            fullWidth
                            noCrop
                        />
                    }

                    {showSunStudy &&
                        <ResidenceExplorerSunStudyView />
                    }

                    <ResidenceExplorerClickArea />

                    {!isMobile && <SelectedObjectPopup parentView={viewReference} />}

                    <UserAgent computer tablet>
                        {currentView && currentView.linked && isDefined(currentView.interLink) && isDefined(currentView.interLink[0]) &&
                            <SliderArrow
                                className="slick-arrow slick-prev"
                                sheer
                                onClick={onPreviousViewClick}
                            />
                        }
                        {currentView && currentView.linked && isDefined(currentView.interLink) && isDefined(currentView.interLink[1]) &&
                            <SliderArrow
                                className="slick-arrow slick-next"
                                sheer
                                next
                                onClick={onNextViewClick}
                            />
                        }
                    </UserAgent>
                </div>
            </div>

            {isMobile && <SelectedObjectPopup parentView={viewReference} />}

            {/*process.env.NODE_ENV === 'development' && //Easter egg
                <span
                    className={
                        new Date().getMinutes() % 6 === 0 ?
                            "sloth-box small" :
                            new Date().getMinutes() % 7 === 0 ?
                                "sloth-box medium" :
                                new Date().getMinutes() % 8 === 0 ?
                                    "sloth-box large" :
                                    "no-sloth"
                    }
                >
                    <span className="sloth" />
                    Sloth <br/>for size
                </span>
            */}
        </div>
    );
};
